import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useUserSave } from '../../../hooks/useUserSave';
import { analyticsService } from '../../../services/analyticsService';
import {
  checkPoppyChatAuth,
  initiatePoppyChat,
} from '../../../transport/api/protectedApi';
import { toLibraryModSettingsState } from '../moderation/libraryModSettingsResolver';
import type { LibraryDialog } from '../libraryRouteTypes';
import { poppyChatFeatures } from '@airhead-games/shared/configs';
import type {
  PoppyChatFeature,
  PoppyChatSettings,
  UserAuthFields,
} from '@airhead-games/shared/types';

/** Where the grant flow returns the host, so they land back on this panel. */
const POPPY_CHAT_PATH = '/library?section=poppy-chat';

const ALL_FEATURES_OFF = Object.fromEntries(
  poppyChatFeatures.map((feature) => [feature, false])
) as PoppyChatSettings;

export type PoppyChatViewModel = {
  features: Record<PoppyChatFeature, boolean>;
  showConnectTwitchRow: boolean;
  onFeatureToggle: (feature: PoppyChatFeature, checked: boolean) => void;
  onViewCommandsClick: () => void;
  onConnectTwitchRowClick: () => void;
};

type UseLibraryPoppyChatParams = {
  user: UserAuthFields | null;
  /** True while the panel is open, which is when the grant is worth checking. */
  isPanelOpen: boolean;
  requireAuth: () => void;
  openDialog: (dialog: LibraryDialog) => void;
};

export type LibraryPoppyChatController = {
  poppyChat: PoppyChatSettings;
  hasTwitchAuth: boolean;
  viewModel: PoppyChatViewModel;
};

function toFeatureUpdate(poppyChat: PoppyChatSettings): PoppyChatSettings {
  return Object.fromEntries(
    poppyChatFeatures.map((feature) => [feature, poppyChat[feature]])
  ) as PoppyChatSettings;
}

export function useLibraryPoppyChat({
  user,
  isPanelOpen,
  requireAuth,
  openDialog,
}: UseLibraryPoppyChatParams): LibraryPoppyChatController {
  const [poppyChat, setPoppyChat] = useState<PoppyChatSettings>(
    () => toLibraryModSettingsState(user).poppyChat
  );
  const [connectTwitchPrompted, setConnectTwitchPrompted] = useState(false);
  // Seeded from the user payload, then settled against Twitch when the panel
  // opens. The server owns the answer; this is only what we last heard.
  const [hasPoppyAuth, setHasPoppyAuth] = useState(
    () => !!user?.hasPoppyChatAuth
  );

  const hasTwitchAuth = !!user?.authTwitch?.userId;

  const { markDirty } = useUserSave({
    value: poppyChat,
    setValue: setPoppyChat,
    toUpdate: (value) => ({
      modSettings: { poppyChat: toFeatureUpdate(value) },
    }),
    fromUser: (nextUser) => toLibraryModSettingsState(nextUser).poppyChat,
    errorMessage: 'Unable to save your Poppy chat settings.',
  });

  useEffect(() => {
    setHasPoppyAuth(!!user?.hasPoppyChatAuth);
  }, [user?.hasPoppyChatAuth]);

  useEffect(() => {
    if (!isPanelOpen || !hasTwitchAuth) {
      return;
    }

    let cancelled = false;
    void checkPoppyChatAuth()
      .then((response) => {
        if (cancelled || response.data?.authorized !== false) {
          return;
        }

        // The host withdrew the app on Twitch. The server has already turned
        // the capabilities off, so mirror that rather than round-tripping.
        setHasPoppyAuth(false);
        setPoppyChat(ALL_FEATURES_OFF);
      })
      .catch(() => {
        // Could not ask. Leave what we have rather than switching the host off
        // because one request failed.
      });

    return () => {
      cancelled = true;
    };
  }, [hasTwitchAuth, isPanelOpen]);

  useEffect(() => {
    if (hasTwitchAuth) {
      setConnectTwitchPrompted(false);
    }
  }, [hasTwitchAuth]);

  const handleFeatureToggle = useCallback(
    (feature: PoppyChatFeature, checked: boolean) => {
      if (!user) {
        requireAuth();
        return;
      }

      // Connecting Twitch belongs to Connections, so point there rather than
      // dragging the host through a link mid-toggle — same as Player Access.
      if (checked && !hasTwitchAuth) {
        setConnectTwitchPrompted(true);
        toast.error(
          'Link your Twitch account in Connections to use this feature.'
        );
        return;
      }

      analyticsService.trackPreferenceChanged({
        preference_type: feature,
        preference_value: checked,
        link_location: 'library_poppy_chat',
      });

      // Switching off never waits on Twitch, and switching on is a plain save
      // whenever Poppy is already authorized here. Only a host without that
      // takes the round trip, and only for the capability they just asked for.
      if (!checked || hasPoppyAuth) {
        setPoppyChat((current) => ({ ...current, [feature]: checked }));
        markDirty();
        return;
      }

      initiatePoppyChat({ feature, redirectTo: POPPY_CHAT_PATH });
    },
    [hasPoppyAuth, hasTwitchAuth, markDirty, requireAuth, user]
  );

  const viewModel = useMemo<PoppyChatViewModel>(
    () => ({
      features: toFeatureUpdate(poppyChat),
      showConnectTwitchRow: !hasTwitchAuth && connectTwitchPrompted,
      onFeatureToggle: handleFeatureToggle,
      onViewCommandsClick: () => {
        analyticsService.trackButtonClick('view_chat_commands', {
          surface: 'library_sidebar',
          link_location: 'library_poppy_chat',
        });
        openDialog('chat-commands');
      },
      onConnectTwitchRowClick: () => openDialog('connections'),
    }),
    [
      connectTwitchPrompted,
      handleFeatureToggle,
      hasTwitchAuth,
      openDialog,
      poppyChat,
    ]
  );

  return useMemo(
    () => ({ poppyChat, hasTwitchAuth, viewModel }),
    [hasTwitchAuth, poppyChat, viewModel]
  );
}
